import React, { useEffect, useMemo, useState } from 'react'
import { AlertCircle, CalendarDays, Check, Hash, Save } from 'lucide-react'

import { supabase } from '../lib/supabase'
import { PodStandupOverrides } from './PodStandupOverrides'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'

interface DeliveryConfigurationProps {
  teamId: string
  userRole: 'owner' | 'admin' | 'member' | 'manager'
}

interface DeliveryConfig {
  digest_enabled: boolean
  digest_time: string
  digest_channel: string | null
}

interface SlackChannel {
  id: string
  name: string
  is_private: boolean
}

const DEFAULT_DELIVERY_CONFIG: DeliveryConfig = {
  digest_enabled: false,
  digest_time: '09:00',
  digest_channel: null,
}

export const DeliveryConfiguration: React.FC<DeliveryConfigurationProps> = ({ teamId, userRole }) => {
  const [config, setConfig] = useState<DeliveryConfig>(DEFAULT_DELIVERY_CONFIG)
  const [channels, setChannels] = useState<SlackChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // ROLE AUDIT (2026-10-09): admin-only, not admin+manager -- but only for
  // the "Team default" digest switch/channel/time below. The manager-
  // accessible path is PodStandupOverrides, mounted unconditionally further
  // down this component and gated on its own (per-pod) manager logic there,
  // not on this isAdmin flag.
  const isAdmin = userRole === 'admin' || userRole === 'owner'

  const loadChannels = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData.session) return

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
        body: JSON.stringify({ team_id: teamId }),
      })

      if (response.ok) setChannels((await response.json()).channels || [])
    } catch (loadError) {
      console.error('Failed to load Slack channels:', loadError)
    }
  }

  useEffect(() => {
    let cancelled = false

    const loadConfiguration = async () => {
      try {
        const { data, error: loadError } = await supabase
          .from('teams')
          .select('standup_config')
          .eq('id', teamId)
          .single() as { data: { standup_config?: Partial<DeliveryConfig> | null } | null; error: any }

        if (loadError) throw loadError
        if (!cancelled && data?.standup_config) {
          setConfig((previous) => ({ ...previous, ...data.standup_config }))
        }
      } catch (loadError: any) {
        if (!cancelled) setError(loadError.message || 'Failed to load delivery settings')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadConfiguration()
    if (isAdmin) void loadChannels()

    return () => {
      cancelled = true
    }
  }, [isAdmin, teamId])

  const selectedChannel = useMemo(
    () => channels.find((channel) => channel.id === config.digest_channel),
    [channels, config.digest_channel],
  )

  const updateConfig = <K extends keyof DeliveryConfig>(key: K, value: DeliveryConfig[K]) => {
    setConfig((previous) => ({ ...previous, [key]: value }))
    setSuccess('')
  }

  const handleSave = async () => {
    if (!isAdmin) return
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      const { data: currentTeam, error: loadError } = await supabase
        .from('teams')
        .select('standup_config')
        .eq('id', teamId)
        .single() as { data: { standup_config?: Record<string, unknown> | null } | null; error: any }

      if (loadError) throw loadError

      const { error: saveError } = await supabase
        .from('teams')
        .update({ standup_config: { ...(currentTeam?.standup_config ?? {}), ...config } } as any)
        .eq('id', teamId)

      if (saveError) throw saveError
      setSuccess('Delivery settings saved successfully.')
    } catch (saveError: any) {
      setError(saveError.message || 'Failed to save delivery settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-background">
        <div className="space-y-3 border-b border-border p-4 sm:p-6">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>
        <div className="flex items-center justify-between gap-6 border-b border-border p-4 sm:p-6">
          <div className="space-y-2"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-56" /></div>
          <Skeleton className="h-10 w-64" />
        </div>
        <div className="flex justify-end p-4 sm:p-6"><Skeleton className="h-10 w-32" /></div>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <div className="border-b border-border p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-foreground">Summary delivery</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose where your team&apos;s daily digest gets posted, and when.</p>
      </div>

      {error && <div role="alert" className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive sm:px-6"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
      {success && <div role="status" className="flex items-center gap-2 border-b border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground sm:px-6"><Check className="h-4 w-4 shrink-0" />{success}</div>}

      <div className="divide-y divide-border">
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-foreground">TD</div>
            <div>
              <div className="text-sm font-medium text-foreground">Team default</div>
              <div className="text-xs text-muted-foreground">{config.digest_enabled ? 'Daily digest enabled' : 'Daily digest disabled'}</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Switch checked={config.digest_enabled} onCheckedChange={(checked) => updateConfig('digest_enabled', checked)} disabled={!isAdmin} aria-label="Daily digest" />
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <Select value={config.digest_channel || 'none'} onValueChange={(value) => updateConfig('digest_channel', value === 'none' ? null : value)} disabled={!isAdmin}>
                <SelectTrigger aria-label="Digest channel" className="w-full sm:w-60"><SelectValue placeholder="Select channel" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select a channel...</SelectItem>{channels.map((channel) => <SelectItem key={channel.id} value={channel.id}>#{channel.name}{channel.is_private ? ' 🔒' : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <Input aria-label="Digest time" type="time" value={config.digest_time} onChange={(event) => updateConfig('digest_time', event.target.value)} disabled={!isAdmin} className="w-full sm:w-32" />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border p-4 sm:p-6">
        <PodStandupOverrides teamId={teamId} />
      </div>

      <div className="flex flex-col gap-3 border-t border-border p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        {!isAdmin && <p className="text-sm text-muted-foreground">Only team admins can modify delivery settings.</p>}
        {selectedChannel && <p className="text-xs text-muted-foreground">Posting to #{selectedChannel.name} at {config.digest_time}</p>}
        <Button onClick={handleSave} disabled={!isAdmin || saving} className="ml-auto min-w-32 bg-primary text-primary-foreground hover:bg-primary/90">
          {saving ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-b-transparent" />Saving...</> : <><Save className="h-4 w-4" />Save Changes</>}
        </Button>
      </div>
    </div>
  )
}
